import { useCallback, useEffect, useRef, useState } from 'react';

// Shared guards for consequential one-tap actions (Wave A — "Trust the sends").
//
// usePendingCommit — a 5s undo window that can NEVER drop the action.
//   The commit callback fires EXACTLY ONCE, on whichever exit happens first:
//     • the timer elapsing (normal path),
//     • component unmount (thread switch / route nav) — commit-on-exit, the fix
//       for the Messages silent send-drop where cleanup cleared the timer and
//       the message evaporated while the UI showed a "sending…" bubble,
//     • pagehide / page hidden (tab close, PWA close, app switch) — callers
//       must make their commit POST with fetch(..., { keepalive: true }) so it
//       survives page teardown.
//   Only cancel() (the user's Undo tap) prevents the commit. A `fired` flag
//   guards the timer-vs-flush race so a flushed commit never double-sends.
//
// useArmedConfirm — two-tap confirm for destructive one-tap actions (Reject /
//   Deny). First tap arms the button ("Tap again to …"), auto-disarms after
//   ~3s; only a second tap while armed runs the action. Keyed, so one hook
//   instance can guard every row in a list.

// ONE undo window portal-wide. usePendingCommit defaults to it, and useToast
// (ui.jsx) shows undo-bearing toasts for exactly this long — before 2026-07-17
// the toast lingered 6s around a 5s window, so an "Undo" could sit on screen
// for a second after the action had already committed.
export const UNDO_WINDOW_SECONDS = 5;

// Irreversible non-reply kinds (Vera P1-2 → shared here for Chat Wave 3 so the
// in-chat approval cards and ConsequenceCard can never diverge on WHICH
// approvals ride the undo window). `consequence.reversible === false` is the
// authoritative proposer signal; the kind list is the floor for older/partial
// proposals that never carried one. Replies are handled separately (they ALWAYS
// get the send window).
export const IRREVERSIBLE_KINDS = ['discount', 'cancel', 'reassign', 'end_membership'];

export function isIrreversibleApproval(action) {
  if (!action || action.kind === 'reply') return false;
  return action.consequence?.reversible === false || IRREVERSIBLE_KINDS.includes(action.kind);
}

// Consequential thread-reply controls require the server's literal affirmative
// canonical-authority receipt. Missing is intentionally NOT legacy-compatible:
// an old/stale row must remain visibly held until the API proves it represents
// the current inbound. Failed rows stay active while Retry is available;
// dismissed failures and historical done/rejected rows are read-only history.
export function canonicalAuthorityBlocked(action) {
  const active = ['proposed', 'approved', 'executing'].includes(action?.status)
    || (action?.status === 'failed' && !action?.dismissed_at);
  const authorityCurrent = action?.proposed_by === 'timeoff-coverage'
    ? action?.timeoff_authority_current === true
    : action?.canonical_authority_current === true;
  return active
    && action?.kind === 'reply'
    && typeof action?.source_ref === 'string'
    && action.source_ref.startsWith('thread:')
    && !authorityCurrent;
}

export function usePendingCommit(seconds = UNDO_WINDOW_SECONDS) {
  // pending: { remaining } while a commit is queued; null otherwise.
  const [pending, setPending] = useState(null);
  // ref holds the live window: { commit, restore, fired, fireT, tickT }.
  const ref = useRef(null);

  // Fire the queued commit now (idempotent — the fired flag guarantees the
  // commit runs at most once no matter how many exit paths race).
  const flush = useCallback((reason) => {
    const p = ref.current;
    if (!p || p.fired) return false;
    p.fired = true;
    clearTimeout(p.fireT);
    clearInterval(p.tickT);
    ref.current = null;
    setPending(null);
    p.commit(reason);
    return true;
  }, []);

  // Undo: abort the queued commit. Returns the `restore` payload passed to
  // start() (or null if nothing was pending) so the caller can put a draft back.
  const cancel = useCallback(() => {
    const p = ref.current;
    if (!p || p.fired) return null;
    p.fired = true;
    clearTimeout(p.fireT);
    clearInterval(p.tickT);
    ref.current = null;
    setPending(null);
    return p.restore ?? true;
  }, []);

  // Open a new undo window. If one is already pending, flush it first — a new
  // send must never silently discard an earlier one.
  const start = useCallback((commit, restore) => {
    flush('replaced');
    let remaining = seconds;
    const p = { commit, restore, fired: false };
    setPending({ remaining });
    p.tickT = setInterval(() => {
      remaining -= 1;
      setPending(remaining > 0 ? { remaining } : null);
    }, 1000);
    p.fireT = setTimeout(() => flush('timer'), seconds * 1000);
    ref.current = p;
  }, [seconds, flush]);

  // Commit-on-exit: unmount (thread switch, route nav) flushes instead of
  // cancelling.
  useEffect(() => () => { flush('unmount'); }, [flush]);

  // Page teardown: pagehide is the reliable close signal; visibilitychange →
  // hidden covers iOS PWA app-switch-then-kill (and background-tab timer
  // throttling). Flushing on hidden ends the undo window early when the user
  // merely switches apps — an accepted trade: committing beats losing the send.
  useEffect(() => {
    const onPageHide = () => flush('pagehide');
    const onVisibility = () => {
      if (document.visibilityState === 'hidden') flush('pagehide');
    };
    window.addEventListener('pagehide', onPageHide);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('pagehide', onPageHide);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [flush]);

  return { pending, start, cancel, flush };
}

// Reworked 2026-08-03 (time-off audit P1-7 — ticket 892b9f9c). The armed state
// used to expire LABEL-ONLY: no countdown, no live-region announcement, and a
// "second" tap after the silent 3s expiry just re-armed — six of Mark's Reject
// taps looked accepted and did nothing. Fixes, all structural:
//   * `armedRemaining` ticks down each second so the expiry is VISIBLE
//     ("Tap again to reject (3)"), and callers can announce it via aria-live.
//   * the armed key lives in a ref, not a state closure — two taps landing in
//     one React batch can no longer BOTH arm.
//   * a confirm tap within CONFIRM_MIN_GAP_MS of arming is ignored (still
//     armed): a double-click must see the armed state before it can fire, or
//     the two-tap guard is theater.
//   * `disarm()` is real — approving clears a pending armed Reject.
const CONFIRM_MIN_GAP_MS = 300;

export function useArmedConfirm(disarmMs = 3000) {
  // {key, remaining} while armed; null otherwise. The ref is the authority
  // (immune to batching); the state is the render mirror.
  const [armed, setArmed] = useState(null);
  const armedRef = useRef(null); // {key, at}
  const timer = useRef(null);
  const tick = useRef(null);

  const clearTimers = () => {
    clearTimeout(timer.current);
    clearInterval(tick.current);
  };

  useEffect(() => () => clearTimers(), []);

  const disarm = useCallback(() => {
    clearTimers();
    armedRef.current = null;
    setArmed(null);
  }, []);

  // confirm(key, fn): first tap arms `key` (returns false); a second tap on the
  // same key while armed disarms and runs fn (returns true).
  const confirm = useCallback((key, fn) => {
    const cur = armedRef.current;
    if (cur && cur.key === key) {
      // Ignore a "confirm" that lands before the armed state could have been
      // seen (double-click / same-batch race) — stay armed, don't fire.
      if (Date.now() - cur.at < CONFIRM_MIN_GAP_MS) return false;
      disarm();
      fn();
      return true;
    }
    clearTimers();
    armedRef.current = { key, at: Date.now() };
    let remaining = Math.max(1, Math.round(disarmMs / 1000));
    setArmed({ key, remaining });
    tick.current = setInterval(() => {
      remaining -= 1;
      if (remaining > 0) setArmed({ key, remaining });
    }, 1000);
    timer.current = setTimeout(() => disarm(), disarmMs);
    return false;
  }, [disarmMs, disarm]);

  return {
    armedKey: armed ? armed.key : null,
    armedRemaining: armed ? armed.remaining : null,
    confirm,
    disarm,
  };
}
